import { useState, useEffect } from "react";

// Supported UI languages. `code` is sent to the chat backend so AI replies match.
export const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "🇬🇧" },
  { code: "nl", label: "Dutch", native: "Nederlands", flag: "🇳🇱" },
  { code: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "French", native: "Français", flag: "🇫🇷" },
  { code: "es", label: "Spanish", native: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Portuguese", native: "Português", flag: "🇵🇹" },
];

const LANG_NAME = {
  en: "English", nl: "Dutch", de: "German", fr: "French",
  es: "Spanish", it: "Italian", pt: "Portuguese",
};

// Translations are keyed by the ENGLISH source string. Missing keys fall back to English.
// {placeholders} are substituted at call time via t(str, { name: "value" }).
export const translations = {
  nl: {
    // Typing status
    "Generating betslip": "Betslip samenstellen",
    "Finding value bets": "Waardebets zoeken",
    "Analyzing players": "Spelers analyseren",
    "Finding matches": "Wedstrijden zoeken",
    "Generating answer": "Antwoord genereren",
    "Thanks for your feedback": "Bedankt voor je feedback",
    "This answer was interrupted, ask again to regenerate the rest.": "Dit antwoord is onderbroken. Stel je vraag opnieuw om de rest te genereren.",
    "View all plans": "Bekijk alle abonnementen",
    "Current plan": "Huidig abonnement",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Om dubbele kosten te voorkomen: annuleer of wijzig eerst je huidige abonnement voordat je een nieuw abonnement afsluit.",
    "MODEL PROB": "MODEL KANS",
    "BetMan is busy right now. Please try again in a few seconds.": "BetMan is momenteel bezet. Probeer het over een paar seconden opnieuw.",
    "What went wrong? (optional)": "Wat ging er mis? (optioneel)",
    "Send": "Versturen",
    // Navigation / tabs
    "Matches": "Wedstrijden",
    "Chat": "Chat",
    "Odds": "Odds",
    "Picks": "Tips",
    "Squad": "Selectie",
    "Form": "Vorm",
    "News": "Nieuws",
    "Back": "Terug",
    // Chat
    "How can I help you today?": "Hoe kan ik je vandaag helpen?",
    "Message BetMan...": "Stuur BetMan een bericht...",
    "Build me a betslip for this weekend": "Stel een betslip samen voor dit weekend",
    "Find mispriced odds for today's matches": "Vind verkeerd geprijsde odds voor de wedstrijden van vandaag",
    "Who is most likely to score in the next match?": "Wie scoort het meest waarschijnlijk in de volgende wedstrijd?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "Welke markt heeft het grootste verschil tussen bookmaker-odds en echte kans?",
    "Build me a value combo — no guesswork, only data-backed picks": "Stel een value-combinatie samen — geen giswerk, alleen door data onderbouwde picks",
    "What are the best football matches to bet on this weekend?": "Wat zijn de beste voetbalwedstrijden om dit weekend op te wedden?",
    "Show me value bets for upcoming matches": "Toon me value bets voor aankomende wedstrijden",
    "Who's most likely to score this weekend?": "Wie scoort dit weekend het meest waarschijnlijk?",
    // Matches tab
    "Upcoming Fixtures": "Aankomende wedstrijden",
    "Fetching live fixtures...": "Live wedstrijden ophalen...",
    "Group": "Groep",
    "Round of 32": "Achtste finales (32)",
    "Round of 16": "Achtste finales",
    "Quarter Finals": "Kwartfinales",
    "Semi Finals": "Halve finales",
    "Final": "Finale",
    "All": "Alle",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Nog geen wedstrijden bevestigd voor deze ronde — wedstrijden verschijnen zodra het schema zich vult.",
    "No matches in this league within the next 14 days.": "Geen wedstrijden in deze competitie binnen de komende 14 dagen.",
    "Could not load fixtures": "Kon wedstrijden niet laden",
    "Retry": "Opnieuw",
    "No upcoming matches": "Geen aankomende wedstrijden",
    "No top-league fixtures found right now. Try refreshing.": "Geen topcompetitiewedstrijden gevonden. Probeer te vernieuwen.",
    "Refresh": "Vernieuwen",
    "Loading odds, form & injuries...": "Odds, vorm & blessures laden...",
    "Tap for picks & analysis →": "Tik voor tips & analyse →",
    "Loading matches...": "Wedstrijden laden...",
    // World Cup banner
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "Het toernooi is LIVE! Tik hieronder op de WK-filter om alle poulewedstrijden te bekijken.",
    "Kick-off in {n} day": "Aftrap over {n} dag",
    "Kick-off in {n} days": "Aftrap over {n} dagen",
    // Picks tab
    "LOW RISK": "LAAG RISICO",
    "MEDIUM": "GEMIDDELD",
    "HIGH RISK": "HOOG RISICO",
    "Prediction data not available": "Voorspellingsdata niet beschikbaar",
    "Check back closer to kickoff": "Kom terug dichter bij de aftrap",
    "Data-driven analysis only. Bet responsibly.": "Alleen datagedreven analyse. Wed verantwoord.",
    // Squad tab
    "Coach:": "Coach:",
    "Starting XI": "Basiself",
    "Substitutes": "Wisselspelers",
    "Lineups not yet announced": "Opstellingen nog niet bekendgemaakt",
    "Usually available ~1 hour before kickoff": "Meestal ~1 uur voor de aftrap beschikbaar",
    "Refresh now": "Nu vernieuwen",
    "Auto-refreshes every 2 min near kickoff": "Ververst automatisch elke 2 min rond de aftrap",
    // Form tab
    "Last 5 matches": "Laatste 5 wedstrijden",
    "Played": "Gespeeld",
    "Wins": "Winst",
    "Draws": "Gelijk",
    "Losses": "Verlies",
    "Goals/G": "Doelp./W",
    "Clean Sh.": "Nul houden",
    "Form data unavailable": "Vormdata niet beschikbaar",
    "Scorers (last 5)": "Doelpuntenmakers (laatste 5)",
    "Loading scorers…": "Doelpuntenmakers laden…",
    // News tab
    "Team news": "Teamnieuws",
    "Latest headlines": "Laatste koppen",
    "Loading headlines…": "Koppen laden…",
    "No recent headlines found": "Geen recente koppen gevonden",
    "Availability": "Beschikbaarheid",
    "No injuries reported — full squad available": "Geen blessures gemeld — volledige selectie beschikbaar",
    // Odds table
    "Home Win": "Thuis wint",
    "Draw": "Gelijkspel",
    "Away Win": "Uit wint",
    "{team} or Draw": "{team} of gelijkspel",
    "{home} or {away}": "{home} of {away}",
    "Over 2.5 Goals": "Meer dan 2,5 doelpunten",
    "BTTS": "Beide teams scoren",
    "Standard odds not loaded yet — search a specific bet below.": "Standaard odds nog niet geladen — zoek hieronder een specifieke weddenschap.",
    "Market": "Markt",
    "Scroll right to see more bookmakers": "Scroll naar rechts voor meer bookmakers",
    "Selection": "Selectie",
    "Best odds highlighted in green · live from bookmakers": "Beste odds groen gemarkeerd · live van bookmakers",
    "Average odds": "Gemiddelde odds",
    "Average odds across bookmakers · live": "Gemiddelde odds over bookmakers · live",
    // Custom bet search
    "Anytime goalscorer": "Doelpuntenmaker (op elk moment)",
    "First goalscorer": "Eerste doelpuntenmaker",
    "Player shot on target": "Schot op doel speler",
    "Team to win 1st half": "Team wint 1e helft",
    "Team to win 2nd half": "Team wint 2e helft",
    "Please log in to search odds.": "Log in om odds te zoeken.",
    "Odds search failed. Try again.": "Zoeken naar odds mislukt. Probeer opnieuw.",
    "SEARCH ANY BET": "ZOEK ELKE WEDDENSCHAP",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Vind elke markt — spelers, hoekschoppen, kaarten, handicaps — met de gemiddelde bookmaker-odds.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "bijv. {player} scoort, meer dan 9,5 hoekschoppen, {team} totaal doelpunten",
    "striker": "spits",
    "Search": "Zoeken",
    "pick a team": "kies een team",
    "pick a player": "kies een speler",
    "Yes, show": "Ja, toon",
    "No": "Nee",
    // Odds compare
    "Odds Comparison": "Odds vergelijken",
    "Scanning betslip...": "Betslip scannen...",
    "Upload your betslip": "Upload je betslip",
    "Drop a screenshot or tap to browse": "Sleep een screenshot of tik om te bladeren",
    "I don't think that's a bet slip, sorry can't help you": "Ik denk niet dat dit een betslip is, sorry, kan je niet helpen",
    "OR BUILD MANUALLY": "OF HANDMATIG SAMENSTELLEN",
    "Build betslip manually": "Betslip handmatig samenstellen",
    "Build Your Betslip": "Stel je betslip samen",
    "Your betslip (reference)": "Je betslip (referentie)",
    "Found {n} selections from your betslip! Review below and compare odds.": "{n} selecties gevonden op je betslip! Bekijk hieronder en vergelijk odds.",
    "{n} selections not yet supported for odds comparison:": "{n} selecties nog niet ondersteund voor oddsvergelijking:",
    "Player props, corners & cards coming soon.": "Spelerstatistieken, hoekschoppen & kaarten komen binnenkort.",
    "League": "Competitie",
    "Your Selections": "Je selecties",
    "Compare Odds Across {n} Platforms": "Vergelijk odds over {n} platforms",
    "Upcoming Matches": "Aankomende wedstrijden",
    "Fetching odds...": "Odds ophalen...",
    "Best Odds Found": "Beste odds gevonden",
    "Edit slip": "Betslip bewerken",
    "YOUR BETSLIP": "JE BETSLIP",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 BESTE GECOMBINEERDE ODDS",
    "BEST VALUE": "BESTE WAARDE",
    "{pct}% less": "{pct}% minder",
    "ALL PLATFORMS": "ALLE PLATFORMS",
    "No platforms found with odds for all your selections. Try removing a selection.": "Geen platforms gevonden met odds voor al je selecties. Probeer een selectie te verwijderen.",
    "POTENTIAL RETURNS (€10 STAKE)": "MOGELIJKE OPBRENGST (€10 INZET)",
    "Best": "Beste",
    "vs Worst shown": "vs slechtste getoond",
    "You save €{amount} by using {bk}": "Je bespaart €{amount} door {bk} te gebruiken",
    "Odds are live and may change. Always verify before placing bets.": "Odds zijn live en kunnen veranderen. Controleer altijd voor je inzet.",
    // Paywall
    "Try the app": "Probeer de app",
    "1 AI chat message per day": "1 AI-chatbericht per dag",
    "View 1 match per day": "Bekijk 1 wedstrijd per dag",
    "Start free": "Gratis starten",
    "Most popular": "Meest populair",
    "Great for starters": "Ideaal om te beginnen",
    "For serious bettors": "Voor serieuze gokkers",
    "Access to News page": "Toegang tot Nieuws-pagina",
    "5 AI chat messages per day": "5 AI-chatberichten per dag",
    "Up to 5 matches per day": "Tot 5 wedstrijden per dag",
    "15 AI chat messages per day": "15 AI-chatberichten per dag",
    "Up to 15 matches per day": "Tot 15 wedstrijden per dag",
    "40 AI chat messages per day": "40 AI-chatberichten per dag",
    "Up to 40 matches per day": "Tot 40 wedstrijden per dag",
    "Try Pro": "Probeer Pro",
    "Go Elite": "Ga Elite",
    "Go Ultimate": "Ga Ultimate",
    "Elite Plan": "Elite-abonnement",
    "Ultimate Plan": "Ultimate-abonnement",
    "Choose your plan": "Kies je abonnement",
    "Cancel anytime · Secure payment via Stripe": "Altijd opzegbaar · Veilig betalen via Stripe",
    "Monthly": "Maandelijks",
    "Yearly": "Jaarlijks",
    "year": "jaar",
    "month": "maand",
    "or {price}/month — save {pct}%": "of {price}/maand — bespaar {pct}%",
    "or {price}/year — save {pct}%": "of {price}/jaar — bespaar {pct}%",
    "Loading...": "Laden...",
    "Your current plan": "Je huidige abonnement",
    "7-day free trial on all paid plans · Cancel anytime": "7 dagen gratis proberen bij alle betaalde abonnementen · Altijd opzegbaar",
    // Auth
    "Log In": "Inloggen",
    "Create Account": "Account aanmaken",
    "Reset Password": "Wachtwoord resetten",
    "Verify Email": "E-mail verifiëren",
    "Menu": "Menu",
    "Check your email": "Controleer je e-mail",
    "We sent an 8-digit code to": "We hebben een 8-cijferige code gestuurd naar",
    "Enter it below to verify your account.": "Voer hem hieronder in om je account te verifiëren.",
    "Verification Code": "Verificatiecode",
    "Enter code": "Voer code in",
    "Verifying...": "Verifiëren...",
    "Verify & Continue": "Verifiëren & doorgaan",
    "Didn't receive a code?": "Geen code ontvangen?",
    "Resend in {n}s": "Opnieuw versturen over {n}s",
    "Resend code": "Code opnieuw versturen",
    "Already verified this email?": "Dit e-mailadres al geverifieerd?",
    "Reset password instead": "Wachtwoord resetten in plaats daarvan",
    "You won't get a code — just log in instead.": "Je ontvangt geen code — log gewoon in.",
    "Back to menu": "Terug naar menu",
    "Enter your email and we'll send you a link to reset your password.": "Voer je e-mail in en we sturen je een link om je wachtwoord te resetten.",
    "Name": "Naam",
    "Your name": "Je naam",
    "Email": "E-mail",
    "Password": "Wachtwoord",
    "Min 6 characters": "Min. 6 tekens",
    "Please wait...": "Even geduld...",
    "Send Reset Link": "Resetlink versturen",
    "Forgot password?": "Wachtwoord vergeten?",
    "Enter verification code": "Verificatiecode invoeren",
    "Back to login": "Terug naar inloggen",
    "Don't have an account? ": "Nog geen account? ",
    "Already have an account? ": "Heb je al een account? ",
    "Sign up": "Aanmelden",
    "Log in": "Inloggen",
    // Account
    "Account": "Account",
    "Free Plan": "Gratis abonnement",
    "Pro Plan": "Pro-abonnement",
    "Premium Plan": "Premium-abonnement",
    "Unlimited": "Onbeperkt",
    "Unlimited AI chat messages": "Onbeperkte AI-chatberichten",
    "Unlimited match analysis": "Onbeperkte wedstrijdanalyse",
    "ACTIVE": "ACTIEF",
    "DAILY USAGE": "DAGELIJKS GEBRUIK",
    "Match Details": "Wedstrijddetails",
    "Odds Compares": "Oddsvergelijkingen",
    "Upgrade to Pro": "Upgrade naar Pro",
    "Upgrade to Elite": "Upgrade naar Elite",
    "Upgrade to Ultimate": "Upgrade naar Ultimate",
    "Upgrade subscription": "Abonnement upgraden",
    "Manage Subscription": "Abonnement beheren",
    "Log Out": "Uitloggen",
    "Join BetMan": "Word lid van BetMan",
    "Sign up to save your preferences and betslips": "Meld je aan om je voorkeuren en betslips op te slaan",
    // App info
    "Language": "Taal",
    "Data-driven betting": "Datagedreven wedden",
    "Terms": "Voorwaarden",
    "Privacy": "Privacy",
    "Refunds": "Terugbetalingen",
    // Polymarket
    "Not in Polymarket WC outright": "Niet in Polymarket WK-eindwinnaar",
    "to win WC": "wint WK",
    "Not in Polymarket group markets": "Niet in Polymarket groepsmarkten",
    "to top Group {group}": "wint groep {group}",
    "Crowd-sourced prediction market": "Crowdsourced voorspellingsmarkt",
    "THIS MATCH — IMPLIED": "DEZE WEDSTRIJD — IMPLICIET",
    "GROUP STAGE — WIN GROUP PROBABILITY": "GROEPSFASE — KANS OM GROEP TE WINNEN",
    "TOURNAMENT — WC WINNER PROBABILITY": "TOERNOOI — KANS OM WK TE WINNEN",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Afgeleid van Polymarkets kansen per groepswinnaar (sterk signaal per wedstrijd)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Afgeleid van Polymarkets WK-winnaarkansen (breed signaal — interpreteer met voorzichtigheid)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Live odds op het land dat het WK voetbal 2026 wint",
  },
  de: {
    "Generating betslip": "Wettschein wird erstellt",
    "Finding value bets": "Value-Wetten suchen",
    "Analyzing players": "Spieler werden analysiert",
    "Finding matches": "Spiele werden gesucht",
    "Generating answer": "Antwort wird erstellt",
    "Thanks for your feedback": "Danke für dein Feedback",
    "This answer was interrupted, ask again to regenerate the rest.": "Diese Antwort wurde unterbrochen. Frag erneut, um den Rest zu generieren.",
    "View all plans": "Alle Tarife ansehen",
    "Current plan": "Aktueller Tarif",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Um doppelte Abbuchungen zu vermeiden: Kündige oder ändere zuerst deinen aktuellen Tarif, bevor du einen neuen kaufst.",
    "MODEL PROB": "MODELL-WAHRSCH.",
    "BetMan is busy right now. Please try again in a few seconds.": "BetMan ist gerade ausgelastet. Bitte versuche es in ein paar Sekunden erneut.",
    "What went wrong? (optional)": "Was lief schief? (optional)",
    "Send": "Senden",
    "Matches": "Spiele",
    "Chat": "Chat",
    "Odds": "Quoten",
    "Picks": "Tipps",
    "Squad": "Kader",
    "Form": "Form",
    "News": "Nachrichten",
    "Back": "Zurück",
    "How can I help you today?": "Wie kann ich dir heute helfen?",
    "Message BetMan...": "Nachricht an BetMan...",
    "Build me a betslip for this weekend": "Erstelle mir einen Wettschein für dieses Wochenende",
    "Find mispriced odds for today's matches": "Finde falsch bepreiste Quoten für die heutigen Spiele",
    "Who is most likely to score in the next match?": "Wer trifft im nächsten Spiel am wahrscheinlichsten?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "Welcher Markt hat die größte Lücke zwischen Buchmacherquoten und echter Wahrscheinlichkeit?",
    "Build me a value combo — no guesswork, only data-backed picks": "Erstelle mir eine Value-Kombi — kein Raten, nur datenbasierte Tipps",
    "What are the best football matches to bet on this weekend?": "Was sind die besten Fußballspiele, auf die man dieses Wochenende wetten kann?",
    "Show me value bets for upcoming matches": "Zeig mir Value-Wetten für kommende Spiele",
    "Who's most likely to score this weekend?": "Wer trifft dieses Wochenende am wahrscheinlichsten?",
    "Upcoming Fixtures": "Kommende Spiele",
    "Fetching live fixtures...": "Live-Spiele werden geladen...",
    "Group": "Gruppe",
    "Round of 32": "Sechzehntelfinale",
    "Round of 16": "Achtelfinale",
    "Quarter Finals": "Viertelfinale",
    "Semi Finals": "Halbfinale",
    "Final": "Finale",
    "All": "Alle",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Noch keine Spiele für diese Runde bestätigt — Spiele erscheinen, sobald sich der Spielplan füllt.",
    "No matches in this league within the next 14 days.": "Keine Spiele in dieser Liga in den nächsten 14 Tagen.",
    "Could not load fixtures": "Spiele konnten nicht geladen werden",
    "Retry": "Erneut versuchen",
    "No upcoming matches": "Keine kommenden Spiele",
    "No top-league fixtures found right now. Try refreshing.": "Derzeit keine Top-Liga-Spiele gefunden. Bitte aktualisieren.",
    "Refresh": "Aktualisieren",
    "Loading odds, form & injuries...": "Quoten, Form & Verletzungen werden geladen...",
    "Tap for picks & analysis →": "Tippen für Tipps & Analyse →",
    "Loading matches...": "Spiele werden geladen...",
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "Das Turnier ist LIVE! Tippe unten auf den WM-Filter, um alle Gruppenspiele zu sehen.",
    "Kick-off in {n} day": "Anstoß in {n} Tag",
    "Kick-off in {n} days": "Anstoß in {n} Tagen",
    "LOW RISK": "GERINGES RISIKO",
    "MEDIUM": "MITTEL",
    "HIGH RISK": "HOHES RISIKO",
    "Prediction data not available": "Vorhersagedaten nicht verfügbar",
    "Check back closer to kickoff": "Schau näher am Anstoß wieder vorbei",
    "Data-driven analysis only. Bet responsibly.": "Nur datenbasierte Analyse. Wette verantwortungsvoll.",
    "Coach:": "Trainer:",
    "Starting XI": "Startelf",
    "Substitutes": "Auswechselspieler",
    "Lineups not yet announced": "Aufstellungen noch nicht bekannt gegeben",
    "Usually available ~1 hour before kickoff": "Normalerweise ~1 Stunde vor Anstoß verfügbar",
    "Refresh now": "Jetzt aktualisieren",
    "Auto-refreshes every 2 min near kickoff": "Aktualisiert automatisch alle 2 Min nahe dem Anstoß",
    "Last 5 matches": "Letzte 5 Spiele",
    "Played": "Spiele",
    "Wins": "Siege",
    "Draws": "Remis",
    "Losses": "Niederl.",
    "Goals/G": "Tore/Sp.",
    "Clean Sh.": "Zu Null",
    "Form data unavailable": "Formdaten nicht verfügbar",
    "Scorers (last 5)": "Torschützen (letzte 5)",
    "Loading scorers…": "Torschützen werden geladen…",
    "Team news": "Team-News",
    "Latest headlines": "Aktuelle Schlagzeilen",
    "Loading headlines…": "Schlagzeilen werden geladen…",
    "No recent headlines found": "Keine aktuellen Schlagzeilen gefunden",
    "Availability": "Verfügbarkeit",
    "No injuries reported — full squad available": "Keine Verletzungen gemeldet — voller Kader verfügbar",
    "Home Win": "Heimsieg",
    "Draw": "Unentschieden",
    "Away Win": "Auswärtssieg",
    "{team} or Draw": "{team} oder Unentschieden",
    "{home} or {away}": "{home} oder {away}",
    "Over 2.5 Goals": "Über 2,5 Tore",
    "BTTS": "Beide Teams treffen",
    "Standard odds not loaded yet — search a specific bet below.": "Standardquoten noch nicht geladen — suche unten eine bestimmte Wette.",
    "Market": "Markt",
    "Scroll right to see more bookmakers": "Nach rechts scrollen für mehr Buchmacher",
    "Selection": "Auswahl",
    "Best odds highlighted in green · live from bookmakers": "Beste Quoten grün markiert · live von Buchmachern",
    "Average odds": "Durchschnittsquote",
    "Average odds across bookmakers · live": "Durchschnittsquote über Buchmacher · live",
    "Anytime goalscorer": "Torschütze (jederzeit)",
    "First goalscorer": "Erster Torschütze",
    "Player shot on target": "Schuss aufs Tor (Spieler)",
    "Team to win 1st half": "Team gewinnt 1. Halbzeit",
    "Team to win 2nd half": "Team gewinnt 2. Halbzeit",
    "Please log in to search odds.": "Bitte melde dich an, um Quoten zu suchen.",
    "Odds search failed. Try again.": "Quotensuche fehlgeschlagen. Bitte erneut versuchen.",
    "SEARCH ANY BET": "BELIEBIGE WETTE SUCHEN",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Finde jeden Markt — Spieler, Ecken, Karten, Handicaps — mit durchschnittlichen Buchmacherquoten.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "z. B. {player} trifft, über 9,5 Ecken, {team} Gesamttore",
    "striker": "Stürmer",
    "Search": "Suchen",
    "pick a team": "Team wählen",
    "pick a player": "Spieler wählen",
    "Yes, show": "Ja, zeigen",
    "No": "Nein",
    "Odds Comparison": "Quotenvergleich",
    "Scanning betslip...": "Wettschein wird gescannt...",
    "Upload your betslip": "Lade deinen Wettschein hoch",
    "Drop a screenshot or tap to browse": "Screenshot ablegen oder tippen zum Durchsuchen",
    "I don't think that's a bet slip, sorry can't help you": "Das scheint kein Wettschein zu sein, tut mir leid, ich kann nicht helfen",
    "OR BUILD MANUALLY": "ODER MANUELL ERSTELLEN",
    "Build betslip manually": "Wettschein manuell erstellen",
    "Build Your Betslip": "Erstelle deinen Wettschein",
    "Your betslip (reference)": "Dein Wettschein (Referenz)",
    "Found {n} selections from your betslip! Review below and compare odds.": "{n} Auswahlen aus deinem Wettschein gefunden! Unten prüfen und Quoten vergleichen.",
    "{n} selections not yet supported for odds comparison:": "{n} Auswahlen werden noch nicht für den Quotenvergleich unterstützt:",
    "Player props, corners & cards coming soon.": "Spielerwetten, Ecken & Karten kommen bald.",
    "League": "Liga",
    "Your Selections": "Deine Auswahlen",
    "Compare Odds Across {n} Platforms": "Quoten über {n} Plattformen vergleichen",
    "Upcoming Matches": "Kommende Spiele",
    "Fetching odds...": "Quoten werden geladen...",
    "Best Odds Found": "Beste Quoten gefunden",
    "Edit slip": "Schein bearbeiten",
    "YOUR BETSLIP": "DEIN WETTSCHEIN",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 BESTE GESAMTQUOTEN",
    "BEST VALUE": "BESTER WERT",
    "{pct}% less": "{pct}% weniger",
    "ALL PLATFORMS": "ALLE PLATTFORMEN",
    "No platforms found with odds for all your selections. Try removing a selection.": "Keine Plattform mit Quoten für alle deine Auswahlen gefunden. Entferne eine Auswahl.",
    "POTENTIAL RETURNS (€10 STAKE)": "MÖGLICHER GEWINN (€10 EINSATZ)",
    "Best": "Beste",
    "vs Worst shown": "vs schlechteste gezeigt",
    "You save €{amount} by using {bk}": "Du sparst €{amount} mit {bk}",
    "Odds are live and may change. Always verify before placing bets.": "Quoten sind live und können sich ändern. Prüfe immer, bevor du wettest.",
    "Try the app": "Teste die App",
    "1 AI chat message per day": "1 KI-Chatnachricht pro Tag",
    "View 1 match per day": "1 Spiel pro Tag ansehen",
    "Start free": "Kostenlos starten",
    "Most popular": "Am beliebtesten",
    "Great for starters": "Ideal für Einsteiger",
    "For serious bettors": "Für ernsthafte Wetter",
    "Access to News page": "Zugang zur News-Seite",
    "5 AI chat messages per day": "5 KI-Chatnachrichten pro Tag",
    "Up to 5 matches per day": "Bis zu 5 Spiele pro Tag",
    "15 AI chat messages per day": "15 KI-Chatnachrichten pro Tag",
    "Up to 15 matches per day": "Bis zu 15 Spiele pro Tag",
    "40 AI chat messages per day": "40 KI-Chatnachrichten pro Tag",
    "Up to 40 matches per day": "Bis zu 40 Spiele pro Tag",
    "Try Pro": "Pro testen",
    "Go Elite": "Elite holen",
    "Go Ultimate": "Ultimate holen",
    "Elite Plan": "Elite-Plan",
    "Ultimate Plan": "Ultimate-Plan",
    "Choose your plan": "Wähle deinen Tarif",
    "Cancel anytime · Secure payment via Stripe": "Jederzeit kündbar · Sichere Zahlung über Stripe",
    "Monthly": "Monatlich",
    "Yearly": "Jährlich",
    "year": "Jahr",
    "month": "Monat",
    "or {price}/month — save {pct}%": "oder {price}/Monat — spare {pct}%",
    "or {price}/year — save {pct}%": "oder {price}/Jahr — spare {pct}%",
    "Loading...": "Lädt...",
    "Your current plan": "Dein aktueller Tarif",
    "7-day free trial on all paid plans · Cancel anytime": "7 Tage kostenlos testen bei allen kostenpflichtigen Tarifen · Jederzeit kündbar",
    "Log In": "Anmelden",
    "Create Account": "Konto erstellen",
    "Reset Password": "Passwort zurücksetzen",
    "Verify Email": "E-Mail bestätigen",
    "Menu": "Menü",
    "Check your email": "Prüfe deine E-Mails",
    "We sent an 8-digit code to": "Wir haben einen 8-stelligen Code gesendet an",
    "Enter it below to verify your account.": "Gib ihn unten ein, um dein Konto zu bestätigen.",
    "Verification Code": "Bestätigungscode",
    "Enter code": "Code eingeben",
    "Verifying...": "Wird bestätigt...",
    "Verify & Continue": "Bestätigen & fortfahren",
    "Didn't receive a code?": "Keinen Code erhalten?",
    "Resend in {n}s": "Erneut senden in {n}s",
    "Resend code": "Code erneut senden",
    "Already verified this email?": "Diese E-Mail bereits bestätigt?",
    "Reset password instead": "Stattdessen Passwort zurücksetzen",
    "You won't get a code — just log in instead.": "Du erhältst keinen Code — melde dich einfach an.",
    "Back to menu": "Zurück zum Menü",
    "Enter your email and we'll send you a link to reset your password.": "Gib deine E-Mail ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.",
    "Name": "Name",
    "Your name": "Dein Name",
    "Email": "E-Mail",
    "Password": "Passwort",
    "Min 6 characters": "Mind. 6 Zeichen",
    "Please wait...": "Bitte warten...",
    "Send Reset Link": "Reset-Link senden",
    "Forgot password?": "Passwort vergessen?",
    "Enter verification code": "Bestätigungscode eingeben",
    "Back to login": "Zurück zur Anmeldung",
    "Don't have an account? ": "Noch kein Konto? ",
    "Already have an account? ": "Schon ein Konto? ",
    "Sign up": "Registrieren",
    "Log in": "Anmelden",
    "Account": "Konto",
    "Free Plan": "Kostenloser Tarif",
    "Pro Plan": "Pro-Tarif",
    "Premium Plan": "Premium-Tarif",
    "Unlimited": "Unbegrenzt",
    "Unlimited AI chat messages": "Unbegrenzte KI-Chatnachrichten",
    "Unlimited match analysis": "Unbegrenzte Spielanalysen",
    "ACTIVE": "AKTIV",
    "DAILY USAGE": "TÄGLICHE NUTZUNG",
    "Match Details": "Spieldetails",
    "Odds Compares": "Quotenvergleiche",
    "Upgrade to Pro": "Upgrade auf Pro",
    "Upgrade to Elite": "Upgrade auf Elite",
    "Upgrade to Ultimate": "Upgrade auf Ultimate",
    "Upgrade subscription": "Abo upgraden",
    "Manage Subscription": "Abo verwalten",
    "Log Out": "Abmelden",
    "Join BetMan": "Tritt BetMan bei",
    "Sign up to save your preferences and betslips": "Registriere dich, um deine Einstellungen und Wettscheine zu speichern",
    "Language": "Sprache",
    "Data-driven betting": "Datenbasiertes Wetten",
    "Terms": "AGB",
    "Privacy": "Datenschutz",
    "Refunds": "Rückerstattungen",
    // Polymarket
    "Not in Polymarket WC outright": "Nicht in Polymarkets WM-Siegerwette",
    "to win WC": "WM-Sieg",
    "Not in Polymarket group markets": "Nicht in Polymarkets Gruppenmärkten",
    "to top Group {group}": "Gruppe {group} gewinnen",
    "Crowd-sourced prediction market": "Crowdsourced-Prognosemarkt",
    "THIS MATCH — IMPLIED": "DIESES SPIEL — IMPLIZIT",
    "GROUP STAGE — WIN GROUP PROBABILITY": "GRUPPENPHASE — WAHRSCHEINLICHKEIT GRUPPENSIEG",
    "TOURNAMENT — WC WINNER PROBABILITY": "TURNIER — WAHRSCHEINLICHKEIT WM-SIEG",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Abgeleitet aus Polymarkets Gruppensieg-Wahrscheinlichkeiten (starkes Signal pro Spiel)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Abgeleitet aus Polymarkets WM-Sieger-Wahrscheinlichkeiten (breites Signal — mit Vorsicht interpretieren)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Live-Quoten auf den Sieger der FIFA Weltmeisterschaft 2026",
  },
  fr: {
    "Generating betslip": "Création du coupon",
    "Finding value bets": "Recherche de value bets",
    "Analyzing players": "Analyse des joueurs",
    "Finding matches": "Recherche de matchs",
    "Generating answer": "Génération de la réponse",
    "Thanks for your feedback": "Merci pour votre retour",
    "This answer was interrupted, ask again to regenerate the rest.": "Cette réponse a été interrompue. Redemandez pour générer la suite.",
    "View all plans": "Voir toutes les formules",
    "Current plan": "Formule actuelle",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Pour éviter une double facturation, annulez ou modifiez d'abord votre formule actuelle avant d'en acheter une nouvelle.",
    "MODEL PROB": "PROB. MODÈLE",
    "BetMan is busy right now. Please try again in a few seconds.": "BetMan est occupé pour le moment. Veuillez réessayer dans quelques secondes.",
    "What went wrong? (optional)": "Qu'est-ce qui n'a pas marché ? (facultatif)",
    "Send": "Envoyer",
    "Matches": "Matchs",
    "Chat": "Chat",
    "Odds": "Cotes",
    "Picks": "Pronostics",
    "Squad": "Effectif",
    "Form": "Forme",
    "News": "Actualités",
    "Back": "Retour",
    "How can I help you today?": "Comment puis-je t'aider aujourd'hui ?",
    "Message BetMan...": "Écris à BetMan...",
    "Build me a betslip for this weekend": "Compose-moi un coupon pour ce week-end",
    "Find mispriced odds for today's matches": "Trouve les cotes mal évaluées pour les matchs d'aujourd'hui",
    "Who is most likely to score in the next match?": "Qui a le plus de chances de marquer au prochain match ?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "Quel marché présente le plus grand écart entre les cotes du bookmaker et la probabilité réelle ?",
    "Build me a value combo — no guesswork, only data-backed picks": "Compose-moi un combiné value — sans approximation, uniquement des choix fondés sur les données",
    "What are the best football matches to bet on this weekend?": "Quels sont les meilleurs matchs de foot sur lesquels parier ce week-end ?",
    "Show me value bets for upcoming matches": "Montre-moi les value bets pour les prochains matchs",
    "Who's most likely to score this weekend?": "Qui a le plus de chances de marquer ce week-end ?",
    "Upcoming Fixtures": "Matchs à venir",
    "Fetching live fixtures...": "Chargement des matchs en direct...",
    "Group": "Groupe",
    "Round of 32": "Seizièmes de finale",
    "Round of 16": "Huitièmes de finale",
    "Quarter Finals": "Quarts de finale",
    "Semi Finals": "Demi-finales",
    "Final": "Finale",
    "All": "Tous",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Aucun match confirmé pour cette phase — les matchs apparaîtront au fur et à mesure du tableau.",
    "No matches in this league within the next 14 days.": "Aucun match dans ce championnat dans les 14 prochains jours.",
    "Could not load fixtures": "Impossible de charger les matchs",
    "Retry": "Réessayer",
    "No upcoming matches": "Aucun match à venir",
    "No top-league fixtures found right now. Try refreshing.": "Aucun match de grand championnat trouvé pour le moment. Essaie d'actualiser.",
    "Refresh": "Actualiser",
    "Loading odds, form & injuries...": "Chargement des cotes, de la forme et des blessures...",
    "Tap for picks & analysis →": "Touche pour les pronostics & l'analyse →",
    "Loading matches...": "Chargement des matchs...",
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "Le tournoi est EN DIRECT ! Appuie sur le filtre Coupe du Monde ci-dessous pour voir tous les matchs de poule.",
    "Kick-off in {n} day": "Coup d'envoi dans {n} jour",
    "Kick-off in {n} days": "Coup d'envoi dans {n} jours",
    "LOW RISK": "FAIBLE RISQUE",
    "MEDIUM": "MOYEN",
    "HIGH RISK": "RISQUE ÉLEVÉ",
    "Prediction data not available": "Données de prédiction non disponibles",
    "Check back closer to kickoff": "Reviens plus près du coup d'envoi",
    "Data-driven analysis only. Bet responsibly.": "Analyse basée sur les données uniquement. Parie de manière responsable.",
    "Coach:": "Entraîneur :",
    "Starting XI": "Onze de départ",
    "Substitutes": "Remplaçants",
    "Lineups not yet announced": "Compositions pas encore annoncées",
    "Usually available ~1 hour before kickoff": "Généralement disponible ~1 heure avant le coup d'envoi",
    "Refresh now": "Actualiser maintenant",
    "Auto-refreshes every 2 min near kickoff": "Actualisation automatique toutes les 2 min près du coup d'envoi",
    "Last 5 matches": "5 derniers matchs",
    "Played": "Joués",
    "Wins": "Victoires",
    "Draws": "Nuls",
    "Losses": "Défaites",
    "Goals/G": "Buts/M",
    "Clean Sh.": "Clean sheets",
    "Form data unavailable": "Données de forme non disponibles",
    "Scorers (last 5)": "Buteurs (5 derniers)",
    "Loading scorers…": "Chargement des buteurs…",
    "Team news": "Infos équipe",
    "Latest headlines": "Dernières actualités",
    "Loading headlines…": "Chargement des actualités…",
    "No recent headlines found": "Aucune actualité récente trouvée",
    "Availability": "Disponibilité",
    "No injuries reported — full squad available": "Aucune blessure signalée — effectif complet disponible",
    "Home Win": "Victoire domicile",
    "Draw": "Match nul",
    "Away Win": "Victoire extérieur",
    "{team} or Draw": "{team} ou nul",
    "{home} or {away}": "{home} ou {away}",
    "Over 2.5 Goals": "Plus de 2,5 buts",
    "BTTS": "Les deux équipes marquent",
    "Standard odds not loaded yet — search a specific bet below.": "Cotes standard pas encore chargées — recherche un pari spécifique ci-dessous.",
    "Market": "Marché",
    "Scroll right to see more bookmakers": "Fais défiler vers la droite pour plus de bookmakers",
    "Selection": "Sélection",
    "Best odds highlighted in green · live from bookmakers": "Meilleures cotes surlignées en vert · en direct des bookmakers",
    "Average odds": "Cote moyenne",
    "Average odds across bookmakers · live": "Cote moyenne entre bookmakers · en direct",
    "Anytime goalscorer": "Buteur (à tout moment)",
    "First goalscorer": "Premier buteur",
    "Player shot on target": "Tir cadré (joueur)",
    "Team to win 1st half": "Équipe gagnante 1re mi-temps",
    "Team to win 2nd half": "Équipe gagnante 2e mi-temps",
    "Please log in to search odds.": "Connecte-toi pour rechercher des cotes.",
    "Odds search failed. Try again.": "Échec de la recherche de cotes. Réessaie.",
    "SEARCH ANY BET": "RECHERCHER N'IMPORTE QUEL PARI",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Trouve n'importe quel marché — joueurs, corners, cartons, handicaps — avec les cotes moyennes des bookmakers.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "ex. {player} marque, plus de 9,5 corners, total de buts {team}",
    "striker": "attaquant",
    "Search": "Rechercher",
    "pick a team": "choisis une équipe",
    "pick a player": "choisis un joueur",
    "Yes, show": "Oui, montrer",
    "No": "Non",
    "Odds Comparison": "Comparaison des cotes",
    "Scanning betslip...": "Analyse du coupon...",
    "Upload your betslip": "Télécharge ton coupon",
    "Drop a screenshot or tap to browse": "Dépose une capture ou touche pour parcourir",
    "I don't think that's a bet slip, sorry can't help you": "Je ne pense pas que ce soit un coupon, désolé, je ne peux pas t'aider",
    "OR BUILD MANUALLY": "OU COMPOSER MANUELLEMENT",
    "Build betslip manually": "Composer le coupon manuellement",
    "Build Your Betslip": "Compose ton coupon",
    "Your betslip (reference)": "Ton coupon (référence)",
    "Found {n} selections from your betslip! Review below and compare odds.": "{n} sélections trouvées sur ton coupon ! Vérifie ci-dessous et compare les cotes.",
    "{n} selections not yet supported for odds comparison:": "{n} sélections pas encore prises en charge pour la comparaison de cotes :",
    "Player props, corners & cards coming soon.": "Paris joueurs, corners & cartons bientôt disponibles.",
    "League": "Championnat",
    "Your Selections": "Tes sélections",
    "Compare Odds Across {n} Platforms": "Comparer les cotes sur {n} plateformes",
    "Upcoming Matches": "Matchs à venir",
    "Fetching odds...": "Chargement des cotes...",
    "Best Odds Found": "Meilleures cotes trouvées",
    "Edit slip": "Modifier le coupon",
    "YOUR BETSLIP": "TON COUPON",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 DES MEILLEURES COTES COMBINÉES",
    "BEST VALUE": "MEILLEURE VALEUR",
    "{pct}% less": "{pct}% de moins",
    "ALL PLATFORMS": "TOUTES LES PLATEFORMES",
    "No platforms found with odds for all your selections. Try removing a selection.": "Aucune plateforme trouvée avec des cotes pour toutes tes sélections. Essaie d'en retirer une.",
    "POTENTIAL RETURNS (€10 STAKE)": "GAINS POTENTIELS (MISE DE €10)",
    "Best": "Meilleure",
    "vs Worst shown": "vs pire affichée",
    "You save €{amount} by using {bk}": "Tu économises €{amount} en utilisant {bk}",
    "Odds are live and may change. Always verify before placing bets.": "Les cotes sont en direct et peuvent changer. Vérifie toujours avant de parier.",
    "Try the app": "Essaie l'appli",
    "1 AI chat message per day": "1 message de chat IA par jour",
    "View 1 match per day": "Voir 1 match par jour",
    "Start free": "Commencer gratuitement",
    "Most popular": "Le plus populaire",
    "Great for starters": "Idéal pour débuter",
    "For serious bettors": "Pour les parieurs sérieux",
    "Access to News page": "Accès à la page Actualités",
    "5 AI chat messages per day": "5 messages de chat IA par jour",
    "Up to 5 matches per day": "Jusqu'à 5 matchs par jour",
    "15 AI chat messages per day": "15 messages de chat IA par jour",
    "Up to 15 matches per day": "Jusqu'à 15 matchs par jour",
    "40 AI chat messages per day": "40 messages de chat IA par jour",
    "Up to 40 matches per day": "Jusqu'à 40 matchs par jour",
    "Try Pro": "Essayer Pro",
    "Go Elite": "Passer Elite",
    "Go Ultimate": "Passer Ultimate",
    "Elite Plan": "Forfait Elite",
    "Ultimate Plan": "Forfait Ultimate",
    "Choose your plan": "Choisis ton forfait",
    "Cancel anytime · Secure payment via Stripe": "Annulable à tout moment · Paiement sécurisé via Stripe",
    "Monthly": "Mensuel",
    "Yearly": "Annuel",
    "year": "an",
    "month": "mois",
    "or {price}/month — save {pct}%": "ou {price}/mois — économise {pct}%",
    "or {price}/year — save {pct}%": "ou {price}/an — économise {pct}%",
    "Loading...": "Chargement...",
    "Your current plan": "Ton forfait actuel",
    "7-day free trial on all paid plans · Cancel anytime": "Essai gratuit de 7 jours sur tous les forfaits payants · Annulable à tout moment",
    "Log In": "Connexion",
    "Create Account": "Créer un compte",
    "Reset Password": "Réinitialiser le mot de passe",
    "Verify Email": "Vérifier l'e-mail",
    "Menu": "Menu",
    "Check your email": "Vérifie tes e-mails",
    "We sent an 8-digit code to": "Nous avons envoyé un code à 8 chiffres à",
    "Enter it below to verify your account.": "Saisis-le ci-dessous pour vérifier ton compte.",
    "Verification Code": "Code de vérification",
    "Enter code": "Saisir le code",
    "Verifying...": "Vérification...",
    "Verify & Continue": "Vérifier & continuer",
    "Didn't receive a code?": "Tu n'as pas reçu de code ?",
    "Resend in {n}s": "Renvoyer dans {n}s",
    "Resend code": "Renvoyer le code",
    "Already verified this email?": "E-mail déjà vérifié ?",
    "Reset password instead": "Réinitialiser le mot de passe plutôt",
    "You won't get a code — just log in instead.": "Vous ne recevrez pas de code — connectez-vous simplement.",
    "Back to menu": "Retour au menu",
    "Enter your email and we'll send you a link to reset your password.": "Saisis ton e-mail et nous t'enverrons un lien pour réinitialiser ton mot de passe.",
    "Name": "Nom",
    "Your name": "Ton nom",
    "Email": "E-mail",
    "Password": "Mot de passe",
    "Min 6 characters": "6 caractères min.",
    "Please wait...": "Patiente...",
    "Send Reset Link": "Envoyer le lien de réinitialisation",
    "Forgot password?": "Mot de passe oublié ?",
    "Enter verification code": "Saisir le code de vérification",
    "Back to login": "Retour à la connexion",
    "Don't have an account? ": "Pas de compte ? ",
    "Already have an account? ": "Déjà un compte ? ",
    "Sign up": "Inscription",
    "Log in": "Connexion",
    "Account": "Compte",
    "Free Plan": "Forfait gratuit",
    "Pro Plan": "Forfait Pro",
    "Premium Plan": "Forfait Premium",
    "Unlimited": "Illimité",
    "Unlimited AI chat messages": "Messages de chat IA illimités",
    "Unlimited match analysis": "Analyses de matchs illimitées",
    "ACTIVE": "ACTIF",
    "DAILY USAGE": "UTILISATION QUOTIDIENNE",
    "Match Details": "Détails du match",
    "Odds Compares": "Comparaisons de cotes",
    "Upgrade to Pro": "Passer à Pro",
    "Upgrade to Elite": "Passer à Elite",
    "Upgrade to Ultimate": "Passer à Ultimate",
    "Upgrade subscription": "Améliorer l'abonnement",
    "Manage Subscription": "Gérer l'abonnement",
    "Log Out": "Déconnexion",
    "Join BetMan": "Rejoins BetMan",
    "Sign up to save your preferences and betslips": "Inscris-toi pour enregistrer tes préférences et tes coupons",
    "Language": "Langue",
    "Data-driven betting": "Paris basés sur les données",
    "Terms": "Conditions",
    "Privacy": "Confidentialité",
    "Refunds": "Remboursements",
    // Polymarket
    "Not in Polymarket WC outright": "Pas dans le vainqueur de la CdM Polymarket",
    "to win WC": "gagne la CdM",
    "Not in Polymarket group markets": "Pas dans les marchés de groupe Polymarket",
    "to top Group {group}": "termine 1er du groupe {group}",
    "Crowd-sourced prediction market": "Marché de prédiction participatif",
    "THIS MATCH — IMPLIED": "CE MATCH — IMPLICITE",
    "GROUP STAGE — WIN GROUP PROBABILITY": "PHASE DE GROUPES — PROBABILITÉ DE GAGNER LE GROUPE",
    "TOURNAMENT — WC WINNER PROBABILITY": "TOURNOI — PROBABILITÉ DE GAGNER LA CdM",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Dérivé des probabilités de vainqueur par groupe de Polymarket (signal fort par match)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Dérivé des probabilités de vainqueur de la CdM de Polymarket (signal large — à interpréter avec prudence)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Cotes en direct sur le pays vainqueur de la Coupe du Monde FIFA 2026",
  },
  es: {
    "Generating betslip": "Generando boleto",
    "Finding value bets": "Buscando value bets",
    "Analyzing players": "Analizando jugadores",
    "Finding matches": "Buscando partidos",
    "Generating answer": "Generando respuesta",
    "Thanks for your feedback": "Gracias por tu opinión",
    "This answer was interrupted, ask again to regenerate the rest.": "Esta respuesta se interrumpió. Pregunta de nuevo para generar el resto.",
    "View all plans": "Ver todos los planes",
    "Current plan": "Plan actual",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Para evitar cobros dobles, cancela o cambia tu plan actual antes de comprar uno nuevo.",
    "MODEL PROB": "PROB. MODELO",
    "BetMan is busy right now. Please try again in a few seconds.": "BetMan está ocupado ahora mismo. Inténtalo de nuevo en unos segundos.",
    "What went wrong? (optional)": "¿Qué salió mal? (opcional)",
    "Send": "Enviar",
    "Matches": "Partidos",
    "Chat": "Chat",
    "Odds": "Cuotas",
    "Picks": "Pronósticos",
    "Squad": "Plantilla",
    "Form": "Forma",
    "News": "Noticias",
    "Back": "Atrás",
    "How can I help you today?": "¿Cómo puedo ayudarte hoy?",
    "Message BetMan...": "Escríbele a BetMan...",
    "Build me a betslip for this weekend": "Crea un boleto de apuestas para este fin de semana",
    "Find mispriced odds for today's matches": "Encuentra cuotas mal valoradas para los partidos de hoy",
    "Who is most likely to score in the next match?": "¿Quién tiene más probabilidades de marcar en el próximo partido?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "¿Qué mercado tiene la mayor diferencia entre las cuotas de la casa y la probabilidad real?",
    "Build me a value combo — no guesswork, only data-backed picks": "Créame una combinada de valor — sin conjeturas, solo selecciones respaldadas por datos",
    "What are the best football matches to bet on this weekend?": "¿Cuáles son los mejores partidos de fútbol para apostar este fin de semana?",
    "Show me value bets for upcoming matches": "Muéstrame apuestas de valor para los próximos partidos",
    "Who's most likely to score this weekend?": "¿Quién tiene más probabilidades de marcar este fin de semana?",
    "Upcoming Fixtures": "Próximos partidos",
    "Fetching live fixtures...": "Cargando partidos en vivo...",
    "Group": "Grupo",
    "Round of 32": "Dieciseisavos de final",
    "Round of 16": "Octavos de final",
    "Quarter Finals": "Cuartos de final",
    "Semi Finals": "Semifinales",
    "Final": "Final",
    "All": "Todos",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Aún no hay partidos confirmados para esta fase — aparecerán a medida que se complete el cuadro.",
    "No matches in this league within the next 14 days.": "No hay partidos en esta liga en los próximos 14 días.",
    "Could not load fixtures": "No se pudieron cargar los partidos",
    "Retry": "Reintentar",
    "No upcoming matches": "No hay próximos partidos",
    "No top-league fixtures found right now. Try refreshing.": "No se encontraron partidos de grandes ligas ahora mismo. Intenta actualizar.",
    "Refresh": "Actualizar",
    "Loading odds, form & injuries...": "Cargando cuotas, forma y lesiones...",
    "Tap for picks & analysis →": "Toca para pronósticos y análisis →",
    "Loading matches...": "Cargando partidos...",
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "¡El torneo está EN VIVO! Toca el filtro del Mundial abajo para ver todos los partidos de la fase de grupos.",
    "Kick-off in {n} day": "Inicio en {n} día",
    "Kick-off in {n} days": "Inicio en {n} días",
    "LOW RISK": "RIESGO BAJO",
    "MEDIUM": "MEDIO",
    "HIGH RISK": "RIESGO ALTO",
    "Prediction data not available": "Datos de predicción no disponibles",
    "Check back closer to kickoff": "Vuelve más cerca del inicio",
    "Data-driven analysis only. Bet responsibly.": "Solo análisis basado en datos. Apuesta con responsabilidad.",
    "Coach:": "Entrenador:",
    "Starting XI": "Once inicial",
    "Substitutes": "Suplentes",
    "Lineups not yet announced": "Alineaciones aún no anunciadas",
    "Usually available ~1 hour before kickoff": "Normalmente disponible ~1 hora antes del inicio",
    "Refresh now": "Actualizar ahora",
    "Auto-refreshes every 2 min near kickoff": "Se actualiza automáticamente cada 2 min cerca del inicio",
    "Last 5 matches": "Últimos 5 partidos",
    "Played": "Jugados",
    "Wins": "Victorias",
    "Draws": "Empates",
    "Losses": "Derrotas",
    "Goals/G": "Goles/P",
    "Clean Sh.": "Porterías a 0",
    "Form data unavailable": "Datos de forma no disponibles",
    "Scorers (last 5)": "Goleadores (últimos 5)",
    "Loading scorers…": "Cargando goleadores…",
    "Team news": "Noticias del equipo",
    "Latest headlines": "Últimos titulares",
    "Loading headlines…": "Cargando titulares…",
    "No recent headlines found": "No se encontraron titulares recientes",
    "Availability": "Disponibilidad",
    "No injuries reported — full squad available": "Sin lesiones reportadas — plantilla completa disponible",
    "Home Win": "Gana local",
    "Draw": "Empate",
    "Away Win": "Gana visitante",
    "{team} or Draw": "{team} o empate",
    "{home} or {away}": "{home} o {away}",
    "Over 2.5 Goals": "Más de 2,5 goles",
    "BTTS": "Ambos equipos marcan",
    "Standard odds not loaded yet — search a specific bet below.": "Las cuotas estándar aún no se cargan — busca una apuesta específica abajo.",
    "Market": "Mercado",
    "Scroll right to see more bookmakers": "Desplázate a la derecha para ver más casas de apuestas",
    "Selection": "Selección",
    "Best odds highlighted in green · live from bookmakers": "Mejores cuotas resaltadas en verde · en vivo de las casas de apuestas",
    "Average odds": "Cuota media",
    "Average odds across bookmakers · live": "Cuota media entre casas de apuestas · en vivo",
    "Anytime goalscorer": "Goleador (en cualquier momento)",
    "First goalscorer": "Primer goleador",
    "Player shot on target": "Tiro a puerta (jugador)",
    "Team to win 1st half": "Equipo gana 1er tiempo",
    "Team to win 2nd half": "Equipo gana 2do tiempo",
    "Please log in to search odds.": "Inicia sesión para buscar cuotas.",
    "Odds search failed. Try again.": "Falló la búsqueda de cuotas. Inténtalo de nuevo.",
    "SEARCH ANY BET": "BUSCA CUALQUIER APUESTA",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Encuentra cualquier mercado — jugadores, córners, tarjetas, hándicaps — con las cuotas medias de las casas de apuestas.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "p. ej. {player} marca, más de 9,5 córners, goles totales de {team}",
    "striker": "delantero",
    "Search": "Buscar",
    "pick a team": "elige un equipo",
    "pick a player": "elige un jugador",
    "Yes, show": "Sí, mostrar",
    "No": "No",
    "Odds Comparison": "Comparación de cuotas",
    "Scanning betslip...": "Escaneando boleto...",
    "Upload your betslip": "Sube tu boleto de apuestas",
    "Drop a screenshot or tap to browse": "Suelta una captura o toca para explorar",
    "I don't think that's a bet slip, sorry can't help you": "No creo que eso sea un boleto, lo siento, no puedo ayudarte",
    "OR BUILD MANUALLY": "O CREAR MANUALMENTE",
    "Build betslip manually": "Crear boleto manualmente",
    "Build Your Betslip": "Crea tu boleto",
    "Your betslip (reference)": "Tu boleto (referencia)",
    "Found {n} selections from your betslip! Review below and compare odds.": "¡Se encontraron {n} selecciones en tu boleto! Revisa abajo y compara cuotas.",
    "{n} selections not yet supported for odds comparison:": "{n} selecciones aún no compatibles para comparación de cuotas:",
    "Player props, corners & cards coming soon.": "Apuestas de jugadores, córners y tarjetas próximamente.",
    "League": "Liga",
    "Your Selections": "Tus selecciones",
    "Compare Odds Across {n} Platforms": "Comparar cuotas en {n} plataformas",
    "Upcoming Matches": "Próximos partidos",
    "Fetching odds...": "Cargando cuotas...",
    "Best Odds Found": "Mejores cuotas encontradas",
    "Edit slip": "Editar boleto",
    "YOUR BETSLIP": "TU BOLETO",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 MEJORES CUOTAS COMBINADAS",
    "BEST VALUE": "MEJOR VALOR",
    "{pct}% less": "{pct}% menos",
    "ALL PLATFORMS": "TODAS LAS PLATAFORMAS",
    "No platforms found with odds for all your selections. Try removing a selection.": "No se encontraron plataformas con cuotas para todas tus selecciones. Intenta quitar una.",
    "POTENTIAL RETURNS (€10 STAKE)": "GANANCIAS POTENCIALES (APUESTA DE €10)",
    "Best": "Mejor",
    "vs Worst shown": "vs peor mostrada",
    "You save €{amount} by using {bk}": "Ahorras €{amount} usando {bk}",
    "Odds are live and may change. Always verify before placing bets.": "Las cuotas son en vivo y pueden cambiar. Verifica siempre antes de apostar.",
    "Try the app": "Prueba la app",
    "1 AI chat message per day": "1 mensaje de chat IA por día",
    "View 1 match per day": "Ver 1 partido por día",
    "Start free": "Empezar gratis",
    "Most popular": "Más popular",
    "Great for starters": "Ideal para empezar",
    "For serious bettors": "Para apostadores serios",
    "Access to News page": "Acceso a la página de Noticias",
    "5 AI chat messages per day": "5 mensajes de chat IA por día",
    "Up to 5 matches per day": "Hasta 5 partidos por día",
    "15 AI chat messages per day": "15 mensajes de chat IA por día",
    "Up to 15 matches per day": "Hasta 15 partidos por día",
    "40 AI chat messages per day": "40 mensajes de chat IA por día",
    "Up to 40 matches per day": "Hasta 40 partidos por día",
    "Try Pro": "Prueba Pro",
    "Go Elite": "Hazte Elite",
    "Go Ultimate": "Hazte Ultimate",
    "Elite Plan": "Plan Elite",
    "Ultimate Plan": "Plan Ultimate",
    "Choose your plan": "Elige tu plan",
    "Cancel anytime · Secure payment via Stripe": "Cancela cuando quieras · Pago seguro vía Stripe",
    "Monthly": "Mensual",
    "Yearly": "Anual",
    "year": "año",
    "month": "mes",
    "or {price}/month — save {pct}%": "o {price}/mes — ahorra {pct}%",
    "or {price}/year — save {pct}%": "o {price}/año — ahorra {pct}%",
    "Loading...": "Cargando...",
    "Your current plan": "Tu plan actual",
    "7-day free trial on all paid plans · Cancel anytime": "Prueba gratis de 7 días en todos los planes de pago · Cancela cuando quieras",
    "Log In": "Iniciar sesión",
    "Create Account": "Crear cuenta",
    "Reset Password": "Restablecer contraseña",
    "Verify Email": "Verificar correo",
    "Menu": "Menú",
    "Check your email": "Revisa tu correo",
    "We sent an 8-digit code to": "Enviamos un código de 8 dígitos a",
    "Enter it below to verify your account.": "Ingrésalo abajo para verificar tu cuenta.",
    "Verification Code": "Código de verificación",
    "Enter code": "Ingresar código",
    "Verifying...": "Verificando...",
    "Verify & Continue": "Verificar y continuar",
    "Didn't receive a code?": "¿No recibiste un código?",
    "Resend in {n}s": "Reenviar en {n}s",
    "Resend code": "Reenviar código",
    "Already verified this email?": "¿Ya verificaste este correo?",
    "Reset password instead": "Restablecer contraseña en su lugar",
    "You won't get a code — just log in instead.": "No recibirás un código — simplemente inicia sesión.",
    "Back to menu": "Volver al menú",
    "Enter your email and we'll send you a link to reset your password.": "Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.",
    "Name": "Nombre",
    "Your name": "Tu nombre",
    "Email": "Correo",
    "Password": "Contraseña",
    "Min 6 characters": "Mín. 6 caracteres",
    "Please wait...": "Espera...",
    "Send Reset Link": "Enviar enlace de restablecimiento",
    "Forgot password?": "¿Olvidaste tu contraseña?",
    "Enter verification code": "Ingresar código de verificación",
    "Back to login": "Volver a iniciar sesión",
    "Don't have an account? ": "¿No tienes cuenta? ",
    "Already have an account? ": "¿Ya tienes cuenta? ",
    "Sign up": "Registrarse",
    "Log in": "Iniciar sesión",
    "Account": "Cuenta",
    "Free Plan": "Plan gratuito",
    "Pro Plan": "Plan Pro",
    "Premium Plan": "Plan Premium",
    "Unlimited": "Ilimitado",
    "Unlimited AI chat messages": "Mensajes de chat IA ilimitados",
    "Unlimited match analysis": "Análisis de partidos ilimitados",
    "ACTIVE": "ACTIVO",
    "DAILY USAGE": "USO DIARIO",
    "Match Details": "Detalles del partido",
    "Odds Compares": "Comparaciones de cuotas",
    "Upgrade to Pro": "Mejorar a Pro",
    "Upgrade to Elite": "Mejorar a Elite",
    "Upgrade to Ultimate": "Mejorar a Ultimate",
    "Upgrade subscription": "Mejorar suscripción",
    "Manage Subscription": "Gestionar suscripción",
    "Log Out": "Cerrar sesión",
    "Join BetMan": "Únete a BetMan",
    "Sign up to save your preferences and betslips": "Regístrate para guardar tus preferencias y boletos",
    "Language": "Idioma",
    "Data-driven betting": "Apuestas basadas en datos",
    "Terms": "Términos",
    "Privacy": "Privacidad",
    "Refunds": "Reembolsos",
    // Polymarket
    "Not in Polymarket WC outright": "No está en el ganador del Mundial de Polymarket",
    "to win WC": "gana el Mundial",
    "Not in Polymarket group markets": "No está en los mercados de grupo de Polymarket",
    "to top Group {group}": "gana el grupo {group}",
    "Crowd-sourced prediction market": "Mercado de predicción colaborativo",
    "THIS MATCH — IMPLIED": "ESTE PARTIDO — IMPLÍCITO",
    "GROUP STAGE — WIN GROUP PROBABILITY": "FASE DE GRUPOS — PROBABILIDAD DE GANAR EL GRUPO",
    "TOURNAMENT — WC WINNER PROBABILITY": "TORNEO — PROBABILIDAD DE GANAR EL MUNDIAL",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Derivado de las probabilidades de ganador por grupo de Polymarket (señal fuerte por partido)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Derivado de las probabilidades de ganador del Mundial de Polymarket (señal amplia — interpretar con cuidado)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Cuotas en vivo sobre el país ganador de la Copa Mundial FIFA 2026",
  },
  it: {
    "Generating betslip": "Creazione schedina",
    "Finding value bets": "Ricerca value bet",
    "Analyzing players": "Analisi giocatori",
    "Finding matches": "Ricerca partite",
    "Generating answer": "Generazione risposta",
    "Thanks for your feedback": "Grazie per il tuo feedback",
    "This answer was interrupted, ask again to regenerate the rest.": "Questa risposta è stata interrotta. Chiedi di nuovo per generare il resto.",
    "View all plans": "Vedi tutti i piani",
    "Current plan": "Piano attuale",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Per evitare doppi addebiti, annulla o modifica il tuo piano attuale prima di acquistarne uno nuovo.",
    "MODEL PROB": "PROB. MODELLO",
    "BetMan is busy right now. Please try again in a few seconds.": "BetMan è occupato in questo momento. Riprova tra qualche secondo.",
    "What went wrong? (optional)": "Cosa non ha funzionato? (facoltativo)",
    "Send": "Invia",
    "Matches": "Partite",
    "Chat": "Chat",
    "Odds": "Quote",
    "Picks": "Pronostici",
    "Squad": "Rosa",
    "Form": "Forma",
    "News": "Notizie",
    "Back": "Indietro",
    "How can I help you today?": "Come posso aiutarti oggi?",
    "Message BetMan...": "Scrivi a BetMan...",
    "Build me a betslip for this weekend": "Creami una schedina per questo weekend",
    "Find mispriced odds for today's matches": "Trova quote mal valutate per le partite di oggi",
    "Who is most likely to score in the next match?": "Chi ha più probabilità di segnare nella prossima partita?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "Quale mercato ha il divario più ampio tra le quote del bookmaker e la probabilità reale?",
    "Build me a value combo — no guesswork, only data-backed picks": "Creami una combo di valore — niente congetture, solo scelte basate sui dati",
    "What are the best football matches to bet on this weekend?": "Quali sono le migliori partite di calcio su cui scommettere questo weekend?",
    "Show me value bets for upcoming matches": "Mostrami le value bet per le prossime partite",
    "Who's most likely to score this weekend?": "Chi ha più probabilità di segnare questo weekend?",
    "Upcoming Fixtures": "Prossime partite",
    "Fetching live fixtures...": "Caricamento partite live...",
    "Group": "Girone",
    "Round of 32": "Sedicesimi di finale",
    "Round of 16": "Ottavi di finale",
    "Quarter Finals": "Quarti di finale",
    "Semi Finals": "Semifinali",
    "Final": "Finale",
    "All": "Tutte",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Nessuna partita confermata per questa fase — le partite appariranno man mano che il tabellone si completa.",
    "No matches in this league within the next 14 days.": "Nessuna partita in questo campionato nei prossimi 14 giorni.",
    "Could not load fixtures": "Impossibile caricare le partite",
    "Retry": "Riprova",
    "No upcoming matches": "Nessuna partita in arrivo",
    "No top-league fixtures found right now. Try refreshing.": "Nessuna partita dei grandi campionati trovata al momento. Prova ad aggiornare.",
    "Refresh": "Aggiorna",
    "Loading odds, form & injuries...": "Caricamento quote, forma e infortuni...",
    "Tap for picks & analysis →": "Tocca per pronostici e analisi →",
    "Loading matches...": "Caricamento partite...",
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "Il torneo è LIVE! Tocca il filtro Mondiali in basso per vedere tutte le partite della fase a gironi.",
    "Kick-off in {n} day": "Calcio d'inizio tra {n} giorno",
    "Kick-off in {n} days": "Calcio d'inizio tra {n} giorni",
    "LOW RISK": "RISCHIO BASSO",
    "MEDIUM": "MEDIO",
    "HIGH RISK": "RISCHIO ALTO",
    "Prediction data not available": "Dati di previsione non disponibili",
    "Check back closer to kickoff": "Ricontrolla più vicino al calcio d'inizio",
    "Data-driven analysis only. Bet responsibly.": "Solo analisi basata sui dati. Scommetti responsabilmente.",
    "Coach:": "Allenatore:",
    "Starting XI": "Formazione titolare",
    "Substitutes": "Riserve",
    "Lineups not yet announced": "Formazioni non ancora annunciate",
    "Usually available ~1 hour before kickoff": "Di solito disponibili ~1 ora prima del calcio d'inizio",
    "Refresh now": "Aggiorna ora",
    "Auto-refreshes every 2 min near kickoff": "Si aggiorna automaticamente ogni 2 min vicino al calcio d'inizio",
    "Last 5 matches": "Ultime 5 partite",
    "Played": "Giocate",
    "Wins": "Vittorie",
    "Draws": "Pareggi",
    "Losses": "Sconfitte",
    "Goals/G": "Gol/P",
    "Clean Sh.": "Porta inviolata",
    "Form data unavailable": "Dati di forma non disponibili",
    "Scorers (last 5)": "Marcatori (ultime 5)",
    "Loading scorers…": "Caricamento marcatori…",
    "Team news": "Notizie squadra",
    "Latest headlines": "Ultime notizie",
    "Loading headlines…": "Caricamento notizie…",
    "No recent headlines found": "Nessuna notizia recente trovata",
    "Availability": "Disponibilità",
    "No injuries reported — full squad available": "Nessun infortunio segnalato — rosa al completo disponibile",
    "Home Win": "Vittoria casa",
    "Draw": "Pareggio",
    "Away Win": "Vittoria trasferta",
    "{team} or Draw": "{team} o pareggio",
    "{home} or {away}": "{home} o {away}",
    "Over 2.5 Goals": "Over 2,5 gol",
    "BTTS": "Entrambe le squadre segnano",
    "Standard odds not loaded yet — search a specific bet below.": "Quote standard non ancora caricate — cerca una scommessa specifica qui sotto.",
    "Market": "Mercato",
    "Scroll right to see more bookmakers": "Scorri a destra per vedere più bookmaker",
    "Selection": "Selezione",
    "Best odds highlighted in green · live from bookmakers": "Migliori quote evidenziate in verde · live dai bookmaker",
    "Average odds": "Quota media",
    "Average odds across bookmakers · live": "Quota media tra i bookmaker · live",
    "Anytime goalscorer": "Marcatore (in qualsiasi momento)",
    "First goalscorer": "Primo marcatore",
    "Player shot on target": "Tiro in porta (giocatore)",
    "Team to win 1st half": "Squadra vince 1° tempo",
    "Team to win 2nd half": "Squadra vince 2° tempo",
    "Please log in to search odds.": "Accedi per cercare le quote.",
    "Odds search failed. Try again.": "Ricerca quote fallita. Riprova.",
    "SEARCH ANY BET": "CERCA QUALSIASI SCOMMESSA",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Trova qualsiasi mercato — giocatori, calci d'angolo, cartellini, handicap — con le quote medie dei bookmaker.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "es. {player} segna, over 9,5 calci d'angolo, gol totali {team}",
    "striker": "attaccante",
    "Search": "Cerca",
    "pick a team": "scegli una squadra",
    "pick a player": "scegli un giocatore",
    "Yes, show": "Sì, mostra",
    "No": "No",
    "Odds Comparison": "Confronto quote",
    "Scanning betslip...": "Scansione schedina...",
    "Upload your betslip": "Carica la tua schedina",
    "Drop a screenshot or tap to browse": "Trascina uno screenshot o tocca per sfogliare",
    "I don't think that's a bet slip, sorry can't help you": "Non credo sia una schedina, mi dispiace, non posso aiutarti",
    "OR BUILD MANUALLY": "O CREA MANUALMENTE",
    "Build betslip manually": "Crea schedina manualmente",
    "Build Your Betslip": "Crea la tua schedina",
    "Your betslip (reference)": "La tua schedina (riferimento)",
    "Found {n} selections from your betslip! Review below and compare odds.": "Trovate {n} selezioni dalla tua schedina! Controlla sotto e confronta le quote.",
    "{n} selections not yet supported for odds comparison:": "{n} selezioni non ancora supportate per il confronto quote:",
    "Player props, corners & cards coming soon.": "Scommesse giocatori, calci d'angolo e cartellini in arrivo.",
    "League": "Campionato",
    "Your Selections": "Le tue selezioni",
    "Compare Odds Across {n} Platforms": "Confronta le quote su {n} piattaforme",
    "Upcoming Matches": "Prossime partite",
    "Fetching odds...": "Caricamento quote...",
    "Best Odds Found": "Migliori quote trovate",
    "Edit slip": "Modifica schedina",
    "YOUR BETSLIP": "LA TUA SCHEDINA",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 MIGLIORI QUOTE COMBINATE",
    "BEST VALUE": "MIGLIOR VALORE",
    "{pct}% less": "{pct}% in meno",
    "ALL PLATFORMS": "TUTTE LE PIATTAFORME",
    "No platforms found with odds for all your selections. Try removing a selection.": "Nessuna piattaforma trovata con quote per tutte le tue selezioni. Prova a rimuoverne una.",
    "POTENTIAL RETURNS (€10 STAKE)": "VINCITE POTENZIALI (PUNTATA DI €10)",
    "Best": "Migliore",
    "vs Worst shown": "vs peggiore mostrata",
    "You save €{amount} by using {bk}": "Risparmi €{amount} usando {bk}",
    "Odds are live and may change. Always verify before placing bets.": "Le quote sono live e possono cambiare. Verifica sempre prima di scommettere.",
    "Try the app": "Prova l'app",
    "1 AI chat message per day": "1 messaggio chat IA al giorno",
    "View 1 match per day": "Vedi 1 partita al giorno",
    "Start free": "Inizia gratis",
    "Most popular": "Più popolare",
    "Great for starters": "Ideale per iniziare",
    "For serious bettors": "Per scommettitori seri",
    "Access to News page": "Accesso alla pagina Notizie",
    "5 AI chat messages per day": "5 messaggi chat IA al giorno",
    "Up to 5 matches per day": "Fino a 5 partite al giorno",
    "15 AI chat messages per day": "15 messaggi chat IA al giorno",
    "Up to 15 matches per day": "Fino a 15 partite al giorno",
    "40 AI chat messages per day": "40 messaggi chat IA al giorno",
    "Up to 40 matches per day": "Fino a 40 partite al giorno",
    "Try Pro": "Prova Pro",
    "Go Elite": "Passa a Elite",
    "Go Ultimate": "Passa a Ultimate",
    "Elite Plan": "Piano Elite",
    "Ultimate Plan": "Piano Ultimate",
    "Choose your plan": "Scegli il tuo piano",
    "Cancel anytime · Secure payment via Stripe": "Disdici quando vuoi · Pagamento sicuro tramite Stripe",
    "Monthly": "Mensile",
    "Yearly": "Annuale",
    "year": "anno",
    "month": "mese",
    "or {price}/month — save {pct}%": "o {price}/mese — risparmia {pct}%",
    "or {price}/year — save {pct}%": "o {price}/anno — risparmia {pct}%",
    "Loading...": "Caricamento...",
    "Your current plan": "Il tuo piano attuale",
    "7-day free trial on all paid plans · Cancel anytime": "Prova gratuita di 7 giorni su tutti i piani a pagamento · Disdici quando vuoi",
    "Log In": "Accedi",
    "Create Account": "Crea account",
    "Reset Password": "Reimposta password",
    "Verify Email": "Verifica email",
    "Menu": "Menu",
    "Check your email": "Controlla la tua email",
    "We sent an 8-digit code to": "Abbiamo inviato un codice a 8 cifre a",
    "Enter it below to verify your account.": "Inseriscilo qui sotto per verificare il tuo account.",
    "Verification Code": "Codice di verifica",
    "Enter code": "Inserisci codice",
    "Verifying...": "Verifica in corso...",
    "Verify & Continue": "Verifica e continua",
    "Didn't receive a code?": "Non hai ricevuto un codice?",
    "Resend in {n}s": "Reinvia tra {n}s",
    "Resend code": "Reinvia codice",
    "Already verified this email?": "Email già verificata?",
    "Reset password instead": "Reimposta password invece",
    "You won't get a code — just log in instead.": "Non riceverai un codice — accedi semplicemente.",
    "Back to menu": "Torna al menu",
    "Enter your email and we'll send you a link to reset your password.": "Inserisci la tua email e ti invieremo un link per reimpostare la password.",
    "Name": "Nome",
    "Your name": "Il tuo nome",
    "Email": "Email",
    "Password": "Password",
    "Min 6 characters": "Min. 6 caratteri",
    "Please wait...": "Attendi...",
    "Send Reset Link": "Invia link di reset",
    "Forgot password?": "Password dimenticata?",
    "Enter verification code": "Inserisci codice di verifica",
    "Back to login": "Torna all'accesso",
    "Don't have an account? ": "Non hai un account? ",
    "Already have an account? ": "Hai già un account? ",
    "Sign up": "Registrati",
    "Log in": "Accedi",
    "Account": "Account",
    "Free Plan": "Piano gratuito",
    "Pro Plan": "Piano Pro",
    "Premium Plan": "Piano Premium",
    "Unlimited": "Illimitato",
    "Unlimited AI chat messages": "Messaggi chat IA illimitati",
    "Unlimited match analysis": "Analisi partite illimitate",
    "ACTIVE": "ATTIVO",
    "DAILY USAGE": "UTILIZZO GIORNALIERO",
    "Match Details": "Dettagli partita",
    "Odds Compares": "Confronti quote",
    "Upgrade to Pro": "Passa a Pro",
    "Upgrade to Elite": "Passa a Elite",
    "Upgrade to Ultimate": "Passa a Ultimate",
    "Upgrade subscription": "Aggiorna abbonamento",
    "Manage Subscription": "Gestisci abbonamento",
    "Log Out": "Esci",
    "Join BetMan": "Unisciti a BetMan",
    "Sign up to save your preferences and betslips": "Registrati per salvare le tue preferenze e schedine",
    "Language": "Lingua",
    "Data-driven betting": "Scommesse basate sui dati",
    "Terms": "Termini",
    "Privacy": "Privacy",
    "Refunds": "Rimborsi",
    // Polymarket
    "Not in Polymarket WC outright": "Non nel vincitore del Mondiale di Polymarket",
    "to win WC": "vince il Mondiale",
    "Not in Polymarket group markets": "Non nei mercati di girone di Polymarket",
    "to top Group {group}": "vince il girone {group}",
    "Crowd-sourced prediction market": "Mercato di previsione collaborativo",
    "THIS MATCH — IMPLIED": "QUESTA PARTITA — IMPLICITA",
    "GROUP STAGE — WIN GROUP PROBABILITY": "FASE A GIRONI — PROBABILITÀ DI VINCERE IL GIRONE",
    "TOURNAMENT — WC WINNER PROBABILITY": "TORNEO — PROBABILITÀ DI VINCERE IL MONDIALE",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Derivato dalle probabilità di vincitore per girone di Polymarket (segnale forte per partita)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Derivato dalle probabilità di vincitore del Mondiale di Polymarket (segnale ampio — da interpretare con cautela)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Quote live sulla nazionale vincitrice della Coppa del Mondo FIFA 2026",
  },
  pt: {
    "Generating betslip": "Gerando boletim",
    "Finding value bets": "Procurando value bets",
    "Analyzing players": "Analisando jogadores",
    "Finding matches": "Procurando jogos",
    "Generating answer": "Gerando resposta",
    "Thanks for your feedback": "Obrigado pelo seu feedback",
    "This answer was interrupted, ask again to regenerate the rest.": "Esta resposta foi interrompida. Pergunte novamente para gerar o resto.",
    "View all plans": "Ver todos os planos",
    "Current plan": "Plano atual",
    "To avoid double charges, cancel or change your current plan before purchasing a new one.": "Para evitar cobranças duplicadas, cancele ou altere o seu plano atual antes de comprar um novo.",
    "MODEL PROB": "PROB. MODELO",
    "BetMan is busy right now. Please try again in a few seconds.": "O BetMan está ocupado agora. Tente novamente em alguns segundos.",
    "What went wrong? (optional)": "O que deu errado? (opcional)",
    "Send": "Enviar",
    "Matches": "Jogos",
    "Chat": "Chat",
    "Odds": "Odds",
    "Picks": "Palpites",
    "Squad": "Elenco",
    "Form": "Forma",
    "News": "Notícias",
    "Back": "Voltar",
    "How can I help you today?": "Como posso ajudar você hoje?",
    "Message BetMan...": "Escreva para o BetMan...",
    "Build me a betslip for this weekend": "Monte um boletim de apostas para este fim de semana",
    "Find mispriced odds for today's matches": "Encontre odds mal precificadas para os jogos de hoje",
    "Who is most likely to score in the next match?": "Quem tem mais probabilidade de marcar no próximo jogo?",
    "Which market has the biggest gap between bookmaker odds and real probability?": "Qual mercado tem a maior diferença entre as odds da casa e a probabilidade real?",
    "Build me a value combo — no guesswork, only data-backed picks": "Monte uma combinada de valor — sem achismos, apenas escolhas baseadas em dados",
    "What are the best football matches to bet on this weekend?": "Quais são os melhores jogos de futebol para apostar neste fim de semana?",
    "Show me value bets for upcoming matches": "Mostre-me apostas de valor para os próximos jogos",
    "Who's most likely to score this weekend?": "Quem tem mais chances de marcar neste fim de semana?",
    "Upcoming Fixtures": "Próximos jogos",
    "Fetching live fixtures...": "Carregando jogos ao vivo...",
    "Group": "Grupo",
    "Round of 32": "Trigésimos de final",
    "Round of 16": "Oitavas de final",
    "Quarter Finals": "Quartas de final",
    "Semi Finals": "Semifinais",
    "Final": "Final",
    "All": "Todos",
    "No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.": "Nenhum jogo confirmado para esta fase ainda — os jogos aparecerão conforme o chaveamento for preenchido.",
    "No matches in this league within the next 14 days.": "Nenhum jogo nesta liga nos próximos 14 dias.",
    "Could not load fixtures": "Não foi possível carregar os jogos",
    "Retry": "Tentar novamente",
    "No upcoming matches": "Nenhum jogo próximo",
    "No top-league fixtures found right now. Try refreshing.": "Nenhum jogo das grandes ligas encontrado agora. Tente atualizar.",
    "Refresh": "Atualizar",
    "Loading odds, form & injuries...": "Carregando odds, forma e lesões...",
    "Tap for picks & analysis →": "Toque para palpites e análise →",
    "Loading matches...": "Carregando jogos...",
    "Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.": "O torneio está AO VIVO! Toque no filtro da Copa do Mundo abaixo para ver todos os jogos da fase de grupos.",
    "Kick-off in {n} day": "Início em {n} dia",
    "Kick-off in {n} days": "Início em {n} dias",
    "LOW RISK": "RISCO BAIXO",
    "MEDIUM": "MÉDIO",
    "HIGH RISK": "RISCO ALTO",
    "Prediction data not available": "Dados de previsão não disponíveis",
    "Check back closer to kickoff": "Volte mais perto do início",
    "Data-driven analysis only. Bet responsibly.": "Apenas análise baseada em dados. Aposte com responsabilidade.",
    "Coach:": "Técnico:",
    "Starting XI": "Time titular",
    "Substitutes": "Reservas",
    "Lineups not yet announced": "Escalações ainda não anunciadas",
    "Usually available ~1 hour before kickoff": "Geralmente disponível ~1 hora antes do início",
    "Refresh now": "Atualizar agora",
    "Auto-refreshes every 2 min near kickoff": "Atualiza automaticamente a cada 2 min perto do início",
    "Last 5 matches": "Últimos 5 jogos",
    "Played": "Jogos",
    "Wins": "Vitórias",
    "Draws": "Empates",
    "Losses": "Derrotas",
    "Goals/G": "Gols/J",
    "Clean Sh.": "Sem sofrer",
    "Form data unavailable": "Dados de forma não disponíveis",
    "Scorers (last 5)": "Goleadores (últimos 5)",
    "Loading scorers…": "Carregando goleadores…",
    "Team news": "Notícias do time",
    "Latest headlines": "Últimas notícias",
    "Loading headlines…": "Carregando notícias…",
    "No recent headlines found": "Nenhuma notícia recente encontrada",
    "Availability": "Disponibilidade",
    "No injuries reported — full squad available": "Nenhuma lesão relatada — elenco completo disponível",
    "Home Win": "Vitória mandante",
    "Draw": "Empate",
    "Away Win": "Vitória visitante",
    "{team} or Draw": "{team} ou empate",
    "{home} or {away}": "{home} ou {away}",
    "Over 2.5 Goals": "Mais de 2,5 gols",
    "BTTS": "Ambas as equipes marcam",
    "Standard odds not loaded yet — search a specific bet below.": "Odds padrão ainda não carregadas — busque uma aposta específica abaixo.",
    "Market": "Mercado",
    "Scroll right to see more bookmakers": "Role para a direita para ver mais casas de apostas",
    "Selection": "Seleção",
    "Best odds highlighted in green · live from bookmakers": "Melhores odds destacadas em verde · ao vivo das casas de apostas",
    "Average odds": "Odd média",
    "Average odds across bookmakers · live": "Odd média entre casas de apostas · ao vivo",
    "Anytime goalscorer": "Goleador (a qualquer momento)",
    "First goalscorer": "Primeiro goleador",
    "Player shot on target": "Chute no gol (jogador)",
    "Team to win 1st half": "Time vence 1º tempo",
    "Team to win 2nd half": "Time vence 2º tempo",
    "Please log in to search odds.": "Faça login para buscar odds.",
    "Odds search failed. Try again.": "Falha na busca de odds. Tente novamente.",
    "SEARCH ANY BET": "BUSQUE QUALQUER APOSTA",
    "Find any market — players, corners, cards, handicaps — with average bookmaker's odds.": "Encontre qualquer mercado — jogadores, escanteios, cartões, handicaps — com as odds médias das casas de apostas.",
    "e.g. {player} to score, over 9.5 corners, {team} total goals": "ex. {player} marca, mais de 9,5 escanteios, total de gols {team}",
    "striker": "atacante",
    "Search": "Buscar",
    "pick a team": "escolha um time",
    "pick a player": "escolha um jogador",
    "Yes, show": "Sim, mostrar",
    "No": "Não",
    "Odds Comparison": "Comparação de odds",
    "Scanning betslip...": "Escaneando boletim...",
    "Upload your betslip": "Envie seu boletim de apostas",
    "Drop a screenshot or tap to browse": "Solte uma captura ou toque para procurar",
    "I don't think that's a bet slip, sorry can't help you": "Acho que isso não é um boletim de apostas, desculpe, não posso ajudar",
    "OR BUILD MANUALLY": "OU MONTAR MANUALMENTE",
    "Build betslip manually": "Montar boletim manualmente",
    "Build Your Betslip": "Monte seu boletim",
    "Your betslip (reference)": "Seu boletim (referência)",
    "Found {n} selections from your betslip! Review below and compare odds.": "Encontradas {n} seleções no seu boletim! Confira abaixo e compare as odds.",
    "{n} selections not yet supported for odds comparison:": "{n} seleções ainda não suportadas para comparação de odds:",
    "Player props, corners & cards coming soon.": "Apostas de jogadores, escanteios e cartões em breve.",
    "League": "Liga",
    "Your Selections": "Suas seleções",
    "Compare Odds Across {n} Platforms": "Comparar odds em {n} plataformas",
    "Upcoming Matches": "Próximos jogos",
    "Fetching odds...": "Carregando odds...",
    "Best Odds Found": "Melhores odds encontradas",
    "Edit slip": "Editar boletim",
    "YOUR BETSLIP": "SEU BOLETIM",
    "TOP 3 BEST COMBINED ODDS": "TOP 3 MELHORES ODDS COMBINADAS",
    "BEST VALUE": "MELHOR VALOR",
    "{pct}% less": "{pct}% menos",
    "ALL PLATFORMS": "TODAS AS PLATAFORMAS",
    "No platforms found with odds for all your selections. Try removing a selection.": "Nenhuma plataforma encontrada com odds para todas as suas seleções. Tente remover uma.",
    "POTENTIAL RETURNS (€10 STAKE)": "RETORNO POTENCIAL (APOSTA DE €10)",
    "Best": "Melhor",
    "vs Worst shown": "vs pior mostrada",
    "You save €{amount} by using {bk}": "Você economiza €{amount} usando {bk}",
    "Odds are live and may change. Always verify before placing bets.": "As odds são ao vivo e podem mudar. Sempre verifique antes de apostar.",
    "Try the app": "Experimente o app",
    "1 AI chat message per day": "1 mensagem de chat IA por dia",
    "View 1 match per day": "Ver 1 jogo por dia",
    "Start free": "Começar grátis",
    "Most popular": "Mais popular",
    "Great for starters": "Ótimo para começar",
    "For serious bettors": "Para apostadores sérios",
    "Access to News page": "Acesso à página de Notícias",
    "5 AI chat messages per day": "5 mensagens de chat IA por dia",
    "Up to 5 matches per day": "Até 5 jogos por dia",
    "15 AI chat messages per day": "15 mensagens de chat IA por dia",
    "Up to 15 matches per day": "Até 15 jogos por dia",
    "40 AI chat messages per day": "40 mensagens de chat IA por dia",
    "Up to 40 matches per day": "Até 40 jogos por dia",
    "Try Pro": "Experimente o Pro",
    "Go Elite": "Seja Elite",
    "Go Ultimate": "Seja Ultimate",
    "Elite Plan": "Plano Elite",
    "Ultimate Plan": "Plano Ultimate",
    "Choose your plan": "Escolha seu plano",
    "Cancel anytime · Secure payment via Stripe": "Cancele quando quiser · Pagamento seguro via Stripe",
    "Monthly": "Mensal",
    "Yearly": "Anual",
    "year": "ano",
    "month": "mês",
    "or {price}/month — save {pct}%": "ou {price}/mês — economize {pct}%",
    "or {price}/year — save {pct}%": "ou {price}/ano — economize {pct}%",
    "Loading...": "Carregando...",
    "Your current plan": "Seu plano atual",
    "7-day free trial on all paid plans · Cancel anytime": "Teste grátis de 7 dias em todos os planos pagos · Cancele quando quiser",
    "Log In": "Entrar",
    "Create Account": "Criar conta",
    "Reset Password": "Redefinir senha",
    "Verify Email": "Verificar e-mail",
    "Menu": "Menu",
    "Check your email": "Verifique seu e-mail",
    "We sent an 8-digit code to": "Enviamos um código de 8 dígitos para",
    "Enter it below to verify your account.": "Digite-o abaixo para verificar sua conta.",
    "Verification Code": "Código de verificação",
    "Enter code": "Digite o código",
    "Verifying...": "Verificando...",
    "Verify & Continue": "Verificar e continuar",
    "Didn't receive a code?": "Não recebeu um código?",
    "Resend in {n}s": "Reenviar em {n}s",
    "Resend code": "Reenviar código",
    "Already verified this email?": "Já verificou este e-mail?",
    "Reset password instead": "Redefinir senha em vez disso",
    "You won't get a code — just log in instead.": "Você não receberá um código — basta fazer login.",
    "Back to menu": "Voltar ao menu",
    "Enter your email and we'll send you a link to reset your password.": "Digite seu e-mail e enviaremos um link para redefinir sua senha.",
    "Name": "Nome",
    "Your name": "Seu nome",
    "Email": "E-mail",
    "Password": "Senha",
    "Min 6 characters": "Mín. 6 caracteres",
    "Please wait...": "Aguarde...",
    "Send Reset Link": "Enviar link de redefinição",
    "Forgot password?": "Esqueceu a senha?",
    "Enter verification code": "Digite o código de verificação",
    "Back to login": "Voltar para o login",
    "Don't have an account? ": "Não tem uma conta? ",
    "Already have an account? ": "Já tem uma conta? ",
    "Sign up": "Cadastrar",
    "Log in": "Entrar",
    "Account": "Conta",
    "Free Plan": "Plano grátis",
    "Pro Plan": "Plano Pro",
    "Premium Plan": "Plano Premium",
    "Unlimited": "Ilimitado",
    "Unlimited AI chat messages": "Mensagens de chat IA ilimitadas",
    "Unlimited match analysis": "Análises de jogos ilimitadas",
    "ACTIVE": "ATIVO",
    "DAILY USAGE": "USO DIÁRIO",
    "Match Details": "Detalhes do jogo",
    "Odds Compares": "Comparações de odds",
    "Upgrade to Pro": "Atualizar para Pro",
    "Upgrade to Elite": "Atualizar para Elite",
    "Upgrade to Ultimate": "Atualizar para Ultimate",
    "Upgrade subscription": "Atualizar assinatura",
    "Manage Subscription": "Gerenciar assinatura",
    "Log Out": "Sair",
    "Join BetMan": "Junte-se ao BetMan",
    "Sign up to save your preferences and betslips": "Cadastre-se para salvar suas preferências e boletins",
    "Language": "Idioma",
    "Data-driven betting": "Apostas baseadas em dados",
    "Terms": "Termos",
    "Privacy": "Privacidade",
    "Refunds": "Reembolsos",
    // Polymarket
    "Not in Polymarket WC outright": "Não está no vencedor da Copa do Mundo da Polymarket",
    "to win WC": "vence a Copa",
    "Not in Polymarket group markets": "Não está nos mercados de grupo da Polymarket",
    "to top Group {group}": "vence o grupo {group}",
    "Crowd-sourced prediction market": "Mercado de previsão colaborativo",
    "THIS MATCH — IMPLIED": "ESTE JOGO — IMPLÍCITO",
    "GROUP STAGE — WIN GROUP PROBABILITY": "FASE DE GRUPOS — PROBABILIDADE DE VENCER O GRUPO",
    "TOURNAMENT — WC WINNER PROBABILITY": "TORNEIO — PROBABILIDADE DE VENCER A COPA",
    "Derived from Polymarket's per-group winner probabilities (strong per-match signal)": "Derivado das probabilidades de vencedor por grupo da Polymarket (sinal forte por jogo)",
    "Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)": "Derivado das probabilidades de vencedor da Copa da Polymarket (sinal amplo — interpretar com cuidado)",
    "Live odds on the country winning the FIFA World Cup 2026 outright": "Odds ao vivo sobre o país vencedor da Copa do Mundo FIFA 2026",
  },
};

// ─── Lightweight subscription store (no Provider needed — works in every component) ───
let currentLang = (typeof localStorage !== "undefined" && localStorage.getItem("betman_lang")) || "en";
const listeners = new Set();

export function getLang() {
  return currentLang;
}
export function getLangName(code = currentLang) {
  return LANG_NAME[code] || "English";
}
export function setLang(code) {
  if (!LANG_NAME[code] || code === currentLang) return;
  currentLang = code;
  try { localStorage.setItem("betman_lang", code); } catch { /* ignore */ }
  listeners.forEach(fn => fn());
}

// Translate an English source string into the active language. {vars} are substituted.
export function t(str, vars) {
  let out = currentLang === "en" ? str : (translations[currentLang]?.[str] ?? str);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      out = out.split(`{${k}}`).join(String(v));
    }
  }
  return out;
}

// Hook: re-renders the calling component whenever the language changes.
export function useLang() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force(n => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return { lang: currentLang, setLang, t, getLangName };
}
